import { useEffect, useRef, useState } from "react";
import { Plus, Pencil, Trash2, Upload, FileText, X, Search, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi, img, money, fmtSize, errMsg, plural } from "../lib/api";
import { Card, Title, Field, Toggle, Modal, Badge, act, uploadImage } from "./kit";

const EMPTY = { name: "", short_desc: "", description: "", price: 0, sale_price: "", images: [], category_ids: [], active: true, featured: false, is_new: false, age: "3–7 let", format: "PDF", pages: "", order: 0 };

function Files({ p, onChange }) {
  const ref = useRef();
  const [prog, setProg] = useState(null);
  const up = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 300 * 1024 * 1024) return toast.error("Soubor je větší než 300 MB");
    const fd = new FormData();
    fd.append("file", f);
    setProg(0);
    try {
      const { data } = await adminApi.post(`/products/${p.id}/files`, fd, { onUploadProgress: (ev) => setProg(Math.round((ev.loaded / (ev.total || 1)) * 100)) });
      onChange([...(p.files || []), data]);
      toast.success("Soubor nahrán");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setProg(null);
      e.target.value = "";
    }
  };
  const del = async (fid) => {
    if (!window.confirm("Smazat soubor?")) return;
    await act(() => adminApi.delete(`/products/${p.id}/files/${fid}`), "Smazáno");
    onChange(p.files.filter((f) => f.id !== fid));
  };
  return (
    <div className="space-y-2" data-testid="product-files">
      {(p.files || []).map((f) => (
        <div key={f.id} className="flex items-center justify-between rounded-2xl bg-[var(--teal-soft)] px-4 py-2 text-sm"><span className="flex items-center gap-2"><FileText className="w-4 h-4" />{f.name} · {fmtSize(f.size)}</span><button onClick={() => del(f.id)} data-testid={`product-file-delete-${f.id}`}><Trash2 className="w-4 h-4" /></button></div>
      ))}
      <button type="button" onClick={() => ref.current.click()} disabled={prog !== null} className="btn-ghost !py-2" data-testid="product-file-upload-btn">{prog !== null ? <><Loader2 className="w-4 h-4 animate-spin" /> {prog} %</> : <><Upload className="w-4 h-4" /> Nahrát soubor ke stažení (max 300 MB)</>}</button>
      <input ref={ref} type="file" hidden onChange={up} />
    </div>
  );
}

function Editor({ item, cats, onClose, onSaved }) {
  const [f, setF] = useState({ ...EMPTY, ...item, sale_price: item?.sale_price || "" });
  const [busy, setBusy] = useState(false);
  const imgRef = useRef();
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const addImages = async (e) => {
    const files = [...e.target.files];
    setBusy(true);
    try {
      const paths = [];
      for (const file of files) paths.push(await uploadImage(file));
      setF((x) => ({ ...x, images: [...x.images, ...paths] }));
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const save = async () => {
    const body = { ...f, price: +f.price || 0, sale_price: f.sale_price ? +f.sale_price : null, order: +f.order || 0 };
    const r = await act(() => (f.id ? adminApi.put(`/products/${f.id}`, body) : adminApi.post("/products", body)), "Uloženo");
    onSaved(r.data);
    if (!f.id) setF({ ...f, ...r.data });
  };
  return (
    <div className="space-y-4" data-testid="product-editor">
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Název" className="sm:col-span-2"><input className="field" value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="product-name-input" /></Field>
        <Field label="Cena (Kč, 0 = zdarma)"><input type="number" className="field" value={f.price} onChange={(e) => set("price", e.target.value)} data-testid="product-price-input" /></Field>
        <Field label="Akční cena (nepovinné)"><input type="number" className="field" value={f.sale_price} onChange={(e) => set("sale_price", e.target.value)} data-testid="product-sale-price-input" /></Field>
        <Field label="Věk"><input className="field" value={f.age} onChange={(e) => set("age", e.target.value)} /></Field>
        <Field label="Počet stran"><input className="field" value={f.pages} onChange={(e) => set("pages", e.target.value)} /></Field>
        <Field label="Krátký popis" className="sm:col-span-2"><input className="field" value={f.short_desc} onChange={(e) => set("short_desc", e.target.value)} data-testid="product-short-input" /></Field>
        <Field label="Popis" className="sm:col-span-2"><textarea rows={6} className="field" value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="product-desc-input" /></Field>
      </div>
      <Field label="Kategorie">
        <div className="flex flex-wrap gap-2">
          {cats.map((c) => {
            const on = f.category_ids.includes(c.id);
            return <button type="button" key={c.id} onClick={() => set("category_ids", on ? f.category_ids.filter((x) => x !== c.id) : [...f.category_ids, c.id])} data-testid={`product-cat-${c.slug}`} className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 ${on ? "bg-[var(--pink)] border-[var(--pink)] text-white" : "border-[#f1e4ea]"}`}>{c.name}</button>;
          })}
        </div>
      </Field>
      <div className="flex flex-wrap gap-6">
        <Toggle checked={f.active} onChange={(v) => set("active", v)} label="Aktivní" testid="product-active-toggle" />
        <Toggle checked={f.is_new} onChange={(v) => set("is_new", v)} label="Novinka (sekce Novinky na hlavní stránce)" testid="product-new-toggle" />
      </div>
      <Field label="Typ produktu">
        <div className="inline-flex rounded-full bg-[var(--blush)] p-1" data-testid="product-type-switch">
          {[[false, "Digitální (PDF ke stažení)"], [true, "Fyzický (posíláme poštou)"]].map(([v, l]) => (
            <button type="button" key={l} onClick={() => set("physical", v)} data-testid={`product-type-${v ? "physical" : "digital"}`} className={`px-4 py-2 rounded-full text-xs font-bold transition-colors ${!!f.physical === v ? "bg-[var(--pink)] text-white" : "text-[var(--muted)]"}`}>{l}</button>
          ))}
        </div>
        {f.physical && <p className="text-xs text-[var(--muted)] mt-2">Zákazník v košíku vybere Zásilkovnu nebo Balíkovnu (Z-BOX / výdejní místo 100 Kč, na adresu 130 Kč).</p>}
      </Field>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="SEO titulek (nepovinné, jinak název)"><input className="field" value={f.seo_title || ""} onChange={(e) => set("seo_title", e.target.value)} data-testid="product-seo-title-input" /></Field>
        <Field label="SEO popis (nepovinné, jinak popis produktu)"><input className="field" value={f.seo_description || ""} onChange={(e) => set("seo_description", e.target.value)} data-testid="product-seo-desc-input" /></Field>
      </div>
      <Field label="Obrázky (první = hlavní)">
        <div className="flex flex-wrap gap-3">
          {f.images.map((p, i) => (
            <div key={p} className="relative w-24 h-24">
              <img src={img(p)} alt="" className="w-full h-full object-cover rounded-2xl" />
              <button type="button" onClick={() => set("images", f.images.filter((x) => x !== p))} className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white shadow grid place-items-center"><X className="w-3 h-3" /></button>
              {i > 0 && <button type="button" onClick={() => set("images", [p, ...f.images.filter((x) => x !== p)])} className="absolute bottom-1 left-1 text-[10px] bg-white/90 rounded-full px-2">hlavní</button>}
            </div>
          ))}
          <button type="button" onClick={() => imgRef.current.click()} className="w-24 h-24 rounded-2xl border-2 border-dashed border-[var(--pink)] grid place-items-center" data-testid="product-image-upload-btn">{busy ? <Loader2 className="animate-spin" /> : <Plus />}</button>
          <input ref={imgRef} type="file" accept="image/*" multiple hidden onChange={addImages} />
        </div>
      </Field>
      <Field label="Soubory ke stažení">{f.id ? <Files p={f} onChange={(files) => { setF({ ...f, files }); onSaved({ ...f, files }); }} /> : <p className="text-sm text-[var(--muted)]">Soubory nahrajete po prvním uložení produktu.</p>}</Field>
      <div className="flex gap-3 justify-end pt-2">
        <button onClick={onClose} className="btn-ghost">Zavřít</button>
        <button onClick={save} className="btn-pink" data-testid="product-save-btn">Uložit produkt</button>
      </div>
    </div>
  );
}

export default function Products() {
  const [list, setList] = useState([]);
  const [cats, setCats] = useState([]);
  const [edit, setEdit] = useState(null);
  const [q, setQ] = useState("");
  const load = () => adminApi.get("/products").then((r) => setList(r.data));
  useEffect(() => {
    load();
    adminApi.get("/categories").then((r) => setCats(r.data));
  }, []);
  const del = async (p) => {
    if (!window.confirm(`Smazat produkt ${p.name}?`)) return;
    await act(() => adminApi.delete(`/products/${p.id}`), "Smazáno");
    load();
  };
  const toggleNew = async (p) => {
    await adminApi.put(`/products/${p.id}`, { is_new: !p.is_new });
    load();
  };
  const shown = list.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <div data-testid="admin-products">
      <Title right={<button className="btn-pink" onClick={() => setEdit({})} data-testid="product-new-btn"><Plus className="w-4 h-4" /> Nový produkt</button>}>Produkty ({list.length})</Title>
      <p className="text-xs text-[var(--muted)] -mt-2 mb-4">Tlačítko Novinka = produkt se zobrazí v sekci Novinky na hlavní stránce (prvních 8).</p>
      <div className="relative max-w-sm mb-4"><Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Hledat" className="field !pl-10" data-testid="admin-product-search" /></div>
      <div className="space-y-2">
        {shown.map((p) => (
          <Card key={p.id} className="!p-3 flex items-center gap-4" data-testid={`admin-product-${p.slug}`}>
            <img src={img(p.images[0])} alt="" className="w-14 h-14 rounded-xl object-cover" />
            <div className="flex-1 min-w-0">
              <p className="font-bold truncate">{p.name}</p>
              <p className="text-xs text-[var(--muted)]">{p.price ? money(p.sale_price || p.price) : "Zdarma"} · {plural(p.files.length, "soubor", "soubory", "souborů")} · prodáno {p.sold}× {p.rating_count > 0 && `· ★ ${p.rating_avg}`}</p>
            </div>
            <div className="hidden md:flex gap-1">{!p.active && <Badge tone="gray">Skryto</Badge>}{p.files.length === 0 && <Badge tone="sun">Bez souboru</Badge>}</div>
            <button onClick={() => toggleNew(p)} title="Novinka (zobrazí se v Novinkách na hlavní stránce)" data-testid={`admin-product-new-${p.slug}`} className={`h-9 px-3 rounded-full text-xs font-bold transition-colors ${p.is_new ? "bg-[var(--sun)] text-[var(--ink)]" : "hover:bg-[var(--blush)] text-[var(--muted)]"}`}>Novinka</button>
            <button onClick={() => setEdit(p)} data-testid={`admin-product-edit-${p.slug}`} className="w-9 h-9 rounded-full hover:bg-[var(--blush)] grid place-items-center"><Pencil className="w-4 h-4" /></button>
            <button onClick={() => del(p)} data-testid={`admin-product-delete-${p.slug}`} className="w-9 h-9 rounded-full hover:bg-[var(--blush)] grid place-items-center"><Trash2 className="w-4 h-4" /></button>
          </Card>
        ))}
      </div>
      <Modal open={!!edit} onClose={() => { setEdit(null); load(); }} title={edit?.id ? "Upravit produkt" : "Nový produkt"} wide>
        {edit && <Editor item={edit} cats={cats} onClose={() => { setEdit(null); load(); }} onSaved={() => load()} />}
      </Modal>
    </div>
  );
}
